import React, { useEffect, useState } from 'react';
import { Tab } from '@headlessui/react';
import { format } from 'date-fns';

import Modal from '../../../../../components/modals/Modal';
import { ListLoader } from '../../../../../components/loaders';
import { Avatar, Button, ErrorLoading, ListNoData } from '../../../../../components/common';
import parentService from '../../../../../services/api/unified-api/parentService';
import delegateService from '../../../../../services/api/unified-api/delegateService';


function ModalParentDetails({ isOpen, parent, onClose }: Props) {
    const [loadingStatus, setLoadingStatus] = useState({ loading: false, error: false, notFound: false });
    const [qrCodeStatus, setQrCodeStatus] = useState({ loading: false, error: false, notFound: false });
    const [parentDetails, setParentDetails] = useState<any>(null);
    const [qrCode, setQrCode] = useState('');


    useEffect(() => {
        if (isOpen && parent?.id) {
            getParent();
        }
    }, [parent]);


    const getParent = async () => {
        setLoadingStatus({ loading: true, error: false, notFound: false });
        const result = await parentService.getParentProfile(parent.id, { includeSelfDelegate: true });
        if (result?.success) {
            setParentDetails(result.data);

            setLoadingStatus({ loading: false, error: false, notFound: false });
        } else {
            if (result.notFound) {
                setLoadingStatus({ loading: false, error: true, notFound: true });
            } else {
                setLoadingStatus({ loading: false, error: true, notFound: false });
            }
        }
    };


    const getQRCode = async (data: any = null) => {
        const parentData = data || parentDetails;
        const delegate = parentData.delegates.find((item: any) => item.primaryContact === parentData.primaryContact);

        if (delegate) {
            setQrCodeStatus({ loading: true, error: false, notFound: false });
            const result = await delegateService.generateQrCode({
                delegateId: delegate.id,
                parentId: parent.id
            }, { forSchool: false });
            if (result?.success) {
                setQrCode(result.data);
                setQrCodeStatus({ loading: false, error: false, notFound: false });
            } else {
                if (result.notFound) {
                    setQrCodeStatus({ loading: false, error: true, notFound: true });
                } else {
                    setQrCodeStatus({ loading: false, error: true, notFound: false });
                }
            }
        }
    };


    return (
        <Modal open={isOpen} onClose={onClose} >
            <div className="inline-block w-screen max-w-3xl">
                <div className="flex justify-between mb-8 px-10 pt-8">
                    <h2 className="font-medium text-xl">Parent Profile</h2>

                    <button type="button" onClick={onClose} className="text-2xl text-gray-500 [&:not(:disabled)]:hover:text-gray-600 dark:text-gray-600 dark:[&:not(:disabled)]:hover:text-gray-500 focus:outline-none">
                        <i className="ri-close-line"></i>
                    </button>
                </div>

                {
                    !loadingStatus.loading && !loadingStatus.error && parentDetails &&
                    <div className='max-h-[80vh] overflow-y-auto px-10 pb-6'>
                        <div className='flex justify-between items-center flex-wrap gap-4 mb-4'>
                            <div className='flex items-center'>
                                <Avatar imageUrl={parentDetails.avatar} name={`${parentDetails?.firstName} ${parentDetails?.otherNames || ''} ${parentDetails?.lastName}`} size={4} className='mr-4' />
                                <div>
                                    <p className='font-medium text-lg '>{parentDetails?.firstName} {parentDetails?.otherNames || ''} {parentDetails?.lastName}</p>
                                    <p className='text-gray-500 dark:text-gray-400'>{parentDetails.primaryContact}</p>
                                </div>
                            </div>

                            {
                                parentDetails.status === "ACTIVE" &&
                                <span className={`inline-block font-medium text-sm uppercase bg-green-500/10 text-green-500 rounded-lg py-1.5 px-3 mr-3`}>{parentDetails.status}</span>
                            }
                            {
                                parentDetails.status !== "ACTIVE" && parentDetails.status !== "SUSPENDED" &&
                                <span className={`inline-block font-medium text-sm uppercase bg-yellow-500/10 text-yellow-500 rounded-lg py-1.5 px-3 mr-3`}>{parentDetails.status}</span>
                            }
                            {
                                parentDetails.status === "SUSPENDED" &&
                                <span className={`inline-block font-medium text-sm uppercase bg-red-500/10 text-red-500 rounded-lg py-1.5 px-3 mr-3`}>{parentDetails.status}</span>
                            }
                        </div>


                        <div className=''>
                            <Tab.Group>
                                <Tab.List as='div' className="border-b dark:border-gray-700">
                                    <Tab as='button' type='button' className="transition-colors duration-300 text-gray-500 dark:text-gray-400 ui-selected:text-blue-500 hocus:text-blue-500 dark:hocus:text-blue-500 dark:ui-selected:text-blue-500 border-b border-transparent ui-selected:border-blue-500 -mb-px py-3 px-6">Details</Tab>
                                    <Tab as='button' type='button' className="transition-colors duration-300 text-gray-500 dark:text-gray-400 ui-selected:text-blue-500 hocus:text-blue-500 dark:hocus:text-blue-500 dark:ui-selected:text-blue-500 border-b border-transparent ui-selected:border-blue-500 -mb-px py-3 px-6">Students</Tab>
                                    <Tab as='button' type='button' className="transition-colors duration-300 text-gray-500 dark:text-gray-400 ui-selected:text-blue-500 hocus:text-blue-500 dark:hocus:text-blue-500 dark:ui-selected:text-blue-500 border-b border-transparent ui-selected:border-blue-500 -mb-px py-3 px-6">Delegates</Tab>
                                    <Tab as='button' type='button' className="transition-colors duration-300 text-gray-500 dark:text-gray-400 ui-selected:text-blue-500 hocus:text-blue-500 dark:hocus:text-blue-500 dark:ui-selected:text-blue-500 border-b border-transparent ui-selected:border-blue-500 -mb-px py-3 px-6">QR Code</Tab>
                                </Tab.List>
                                <Tab.Panels as='div' className="pb-6">
                                    <Tab.Panel unmount={false} as='div' className="divide-y dark:divide-gray-800 px-4">
                                        <div className='pt-4'>
                                            <div className='grid grid-cols-2 gap-x-4'>
                                                <div className='py-4'>
                                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Email</p>
                                                    <p>{parentDetails?.email || 'N/A'}</p>
                                                </div>
                                                <div className='py-4'>
                                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>App Installed</p>
                                                    <p>{parentDetails.installedApp ? 'True' : 'False'}</p>
                                                </div>
                                            </div>

                                            <div className='grid grid-cols-2 gap-x-4'>
                                                <div className='py-4'>
                                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Parent Type</p>
                                                    <p>{parentDetails?.parentType || 'N/A'}</p>
                                                </div>
                                                <div className='py-4'>
                                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Delegate Type</p>
                                                    <p>{parentDetails.delegateType}</p>
                                                </div>
                                            </div>

                                            <div className='grid grid-cols-2 gap-x-4'>
                                                <div className='py-4'>
                                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Date Created</p>
                                                    <p>{format(new Date(parentDetails?.createdDate), 'do LLLL yyyy h:mm a')}</p>
                                                </div>

                                                <div className='py-4'>
                                                    <p className='font-medium text-sm text-gray-500 dark:text-gray-400 mb-1'>Last Updated</p>
                                                    <p>{parentDetails?.updatedDate ? format(new Date(parentDetails?.updatedDate), 'do LLLL yyyy h:mm a') : 'N/A'}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </Tab.Panel>
                                    <Tab.Panel unmount={false} as='div' className="pt-6">
                                        {
                                            parentDetails.children && parentDetails.children.length > 0 &&
                                            <div className='max-w-full overflow-x-auto mb-6'>
                                                <table className="border-collapse table-auto text-sm w-full">
                                                    <thead>
                                                        <tr className='text-left border-b dark:border-gray-700'>
                                                            <th className='py-3 px-4'>No.</th>
                                                            <th className='py-3 px-4'>Name</th>
                                                            <th className='py-3 px-4'>Class</th>
                                                            <th className='py-3 px-4'>Campus</th>
                                                            {/* <th className='py-3 px-4'>Gender</th> */}
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y dark:divide-gray-700/60">
                                                        {
                                                            parentDetails.children.map((studentItem: any, studentItemIndex: number) =>
                                                                <tr key={studentItem.id}>
                                                                    <td className='whitespace-nowrap py-3 px-4'>
                                                                        {studentItemIndex + 1}
                                                                    </td>
                                                                    <td className='py-3 px-4'>
                                                                        {studentItem.firstName} {studentItem.otherNames} {studentItem.lastName}
                                                                    </td>

                                                                    <td className='py-3 px-4'>
                                                                        {studentItem.grade.campus.name} <span className='text-gray-500 dark:text-gray-400'>({studentItem.grade.campus.alias})</span>
                                                                    </td>
                                                                    <td className='py-3 px-4'>
                                                                        {studentItem.grade.name} <span className='text-gray-500 dark:text-gray-400'>{studentItem.grade.code}</span>
                                                                    </td>
                                                                    {/*  <td className='py-3 px-4'>
                                                                        {studentItem.gender || 'N/A'}
                                                                    </td> */}
                                                                </tr>
                                                            )
                                                        }
                                                    </tbody>
                                                </table>
                                            </div>
                                        }

                                        {
                                            !parentDetails.children &&
                                            <ListNoData title='No Student Assigned' description={`No student assigned to ${parentDetails.fullName}`} className='!pt-10 !pb-0'>
                                            </ListNoData>
                                        }
                                    </Tab.Panel>
                                    <Tab.Panel unmount={false} as='div' className="pt-6">
                                        {
                                            parentDetails.delegates && parentDetails.delegates.length > 0 &&
                                            <div className='max-w-full overflow-x-auto mb-6'>
                                                <table className="border-collapse table-auto text-sm w-full">
                                                    <thead>
                                                        <tr className='text-left border-b dark:border-gray-700'>
                                                            <th className='py-3 px-4'>No.</th>
                                                            <th className='py-3 px-4'>Name</th>
                                                            <th className='whitespace-nowrap py-3 px-4'>Primary Contact</th>
                                                            <th className='py-3 px-4'>Relation</th>
                                                            <th className='py-3 px-4'>Status</th>
                                                            {/* <th className='py-3 px-4'>Gender</th> */}
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y dark:divide-gray-700/60">
                                                        {
                                                            parentDetails.delegates
                                                                .filter((item: any) => item.primaryContact !== parentDetails.primaryContact)
                                                                .map((delegateItem: any, delegateItemIndex: number) =>
                                                                    <tr key={delegateItem.id}>
                                                                        <td className='whitespace-nowrap py-3 px-4'>{delegateItemIndex + 1}</td>
                                                                        <td className='py-3 px-4'>{delegateItem.fullName}</td>
                                                                        <td className='py-3 px-4'>{delegateItem.primaryContact}</td>
                                                                        <td className='py-3 px-4'>{delegateItem.relation}</td>
                                                                        <td className='py-3 px-4'>
                                                                            {
                                                                                delegateItem.status === "ACTIVE" &&
                                                                                <span className={`inline-block font-medium text-sm uppercase bg-green-500/10 text-green-500 rounded-lg py-1 px-3 mr-3`}>{delegateItem.status}</span>
                                                                            }
                                                                            {
                                                                                delegateItem.status !== "ACTIVE" && delegateItem.status !== "SUSPENDED" &&
                                                                                <span className={`inline-block font-medium text-sm uppercase bg-yellow-500/10 text-yellow-500 rounded-lg py-1 px-3 mr-3`}>{delegateItem.status}</span>
                                                                            }
                                                                            {
                                                                                delegateItem.status === "SUSPENDED" &&
                                                                                <span className={`inline-block font-medium text-sm uppercase bg-red-500/10 text-red-500 rounded-lg py-1 px-3 mr-3`}>{delegateItem.status}</span>
                                                                            }
                                                                        </td>
                                                                    </tr>
                                                                )
                                                        }
                                                    </tbody>
                                                </table>
                                            </div>
                                        }

                                        {
                                            !parentDetails.delegates &&
                                            <ListNoData title='No Delegate Found' description="No delegate attached to parent" className='!pt-10 !pb-0'>
                                            </ListNoData>
                                        }
                                    </Tab.Panel>
                                    <Tab.Panel unmount={false} as='div' className="pt-6">
                                        {
                                            !qrCodeStatus.loading && !qrCode &&
                                            <ListNoData title='' showIcon={false} description={`Click the button below to generate QR COde`} className='!pt-10 pb-20'>
                                                <Button type='button' onClick={() => getQRCode()} className='py-2'>Generate QR Code</Button>
                                            </ListNoData>
                                        }
                                        {
                                            !qrCodeStatus.loading && qrCode &&
                                            <div className='text-center'>
                                                <img src={qrCode} className='inline-block max-w-xs mb-4' alt="Delegate QR Code" />
                                                <div>
                                                    <a href={qrCode} download={`qrcode-for-delegate-${parentDetails.fullName}.png`} className='transition-colors text-blue-500 hover:underline'>Download QR Code</a>
                                                </div>
                                            </div>
                                        }
                                        {
                                            qrCodeStatus.loading &&
                                            <ListLoader loadingText='Loading QR Code' className='py-10' loaderSize={2} />
                                        }
                                        {
                                            qrCodeStatus.error && qrCodeStatus.notFound &&
                                            <ListNoData title='No QR Code Found' showIcon={false} description={`No QR Code found for ${parentDetails.fullName}`} className='!pt-10 !pb-0'>
                                            </ListNoData>
                                        }
                                        {
                                            qrCodeStatus.error && !qrCodeStatus.notFound &&
                                            <ErrorLoading title='Error Loading QR Code' showIcon={false} message='An unexpected error occurred while loading QR Code' className='px-8 py-16' onTryAgain={() => getQRCode()} />
                                        }
                                    </Tab.Panel>
                                </Tab.Panels>
                            </Tab.Group>
                        </div>

                        <div className='flex justify-end items-center'>
                            <Button type='button' onClick={onClose} className='!text-gray-500 dark:!text-white bg-gray-100 hocus:bg-gray-200  dark:bg-gray-800 dark:hocus:bg-gray-600 py-2.5'>
                                Close
                            </Button>
                        </div>
                    </div>
                }

                {
                    (loadingStatus.loading || loadingStatus.error) &&
                    <div className='pb-10'>
                        {
                            loadingStatus.loading &&
                            <ListLoader loadingText='Loading Profile' className='py-10' loaderSize={2} />
                        }
                        {
                            loadingStatus.error && !loadingStatus.notFound &&
                            <ErrorLoading title='Error Loading Profile' message="An unexpected error occurred while loading parent profile" className='px-8 py-16' onTryAgain={getParent} />
                        }
                        {
                            loadingStatus.error && loadingStatus.notFound &&
                            <ErrorLoading title='Profile Not Found' message="Parent may have been removed or never existed" className='px-8 py-16' />
                        }
                    </div>
                }
            </div>
        </Modal>
    );
}

interface Props {
    isOpen: boolean;
    parent: any;
    onClose: () => void;
}

export default ModalParentDetails;